import Icon from '@/components/ui/AppIcon';

interface CoreValue {
  icon: string;
  title: string;
  description: string;
  color: string;
}

export default function CoreValuesSection() {
  const coreValues: CoreValue[] = [
    {
      icon: 'HeartIcon',
      title: 'Community-Centered Impact',
      description: 'We design programs in partnership with the communities we serve, ensuring our efforts reflect real needs and create meaningful, long-term impact.',
      color: 'error'
    },
    {
      icon: 'AcademicCapIcon',
      title: 'Education as Empowerment',
      description: 'We empower communities with knowledge and skills in nutrition, food science, and food systems through hands-on, accessible education.',
      color: 'brand-primary'
    },
    {
      icon: 'SparklesIcon',
      title: 'Purpose-Driven Innovation',
      description: 'We disrupt traditional food distribution through localized agricultural technology that serves food-insecure communities while supporting education and sustainable production.',
      color: 'accent'
    },
    {
      icon: 'GlobeAltIcon',
      title: 'Reliable Access to Food',
      description: 'We create localized sources of high-quality produce that reduce waste, strengthen community food access, and supplement diets of individuals and families in need.',
      color: 'success'
    },
    {
      icon: 'ChartBarIcon',
      title: 'Accountability and Measurable Impact',
      description: 'We track progress through clear milestones and measurable outcomes to ensure our programs deliver real value and remain aligned with our mission.',
      color: 'brand-secondary'
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-background via-surface to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary/10 rounded-full mb-6">
            <Icon name="StarIcon" size={20} className="text-brand-primary" variant="solid" />
            <span className="text-sm font-semibold text-brand-primary">Core Values</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            What Drives Us Forward
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Our work is grounded in a clear mission and guided by the needs of the communities we serve. Every program we build and every decision we make is rooted in our commitment to empowering food-insecure communities with resilient and reliable resources.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:justify-items-center">
          {coreValues.map((value, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-8 shadow-brand-md hover:shadow-brand-lg hover:-translate-y-2 transition-all duration-300 group w-full lg:max-w-sm"
            >
              <div className={`w-16 h-16 bg-${value.color}/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <Icon 
                  name={value.icon as any} 
                  size={32} 
                  className={`text-${value.color}`}
                  variant="solid"
                />
              </div>
              
              <h3 className="text-xl font-bold text-text-primary mb-3">
                {value.title}
              </h3>
              
              <p className="text-text-secondary leading-relaxed">
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}